/**
 * Speicher-Adapter — die Aussenschnittstelle zum Cloud-Ordner.
 *
 * Ein Adapter kann zweierlei:
 *
 *   lies()          -> Abzug oder null, wenn dort noch nichts liegt
 *   schreib(abzug)  -> legt den Abzug ab
 *
 * Mehr braucht der Sync nicht, und mehr soll er auch nicht wissen. Damit lässt
 * sich Google Drive gegen Dropbox tauschen, ohne die Logik anzufassen
 * (CLAUDE.md: "Jede Aussenschnittstelle ist ein Adapter").
 *
 * **Was geprüft ist und was nicht.** `fetch` und die Weiterleitung werden
 * hereingereicht, nicht global genommen. Dadurch sind Anfragepfade, Kopfzeilen,
 * Nutzlast und Fehlerbehandlung beider Cloud-Adapter durch Tests abgedeckt.
 * Ungeprüft bleibt allein das, was ohne Konto nicht geht: der echte
 * Netzverkehr und der Zustimmungsdialog des Anbieters.
 */

import { pruefeAbzug } from './sync.js';

export const DATEINAME = 'tauchlogbuch-checkin.json';

/**
 * Womit der Adapter prüft, was er gelesen hat.
 *
 * Vorgabe ist der Abzug des Abgleichs. Die Sicherungskopie liegt im selben
 * App-Ordner, trägt aber ein anderes Format — ohne diesen Haken lehnte der
 * Adapter sie mit „Erwartet wird tauchlogbuch-sync/1" ab, obwohl die Datei in
 * Ordnung ist. Geprüft wird weiterhin; nur gegen das richtige Schema.
 */
function prueferFuer(pruefe, bereich) {
  return typeof pruefe === 'function' ? pruefe : (daten) => pruefeAbzug(daten, bereich);
}

// ------------------------------------------------------------------ Datei

/** Datei-Adapter: der Notausgang, der immer funktioniert. */
export const dateiAdapter = {
  name: 'Datei',
  // `pruefe` wie bei den Cloud-Adaptern: Die Sicherungskopie traegt ein
  // anderes Format als der Abzug des Abgleichs und wuerde sonst abgewiesen.
  async lies(datei, pruefe = null) {
    if (!datei) return null;
    const text = datei.name.endsWith('.gz')
      ? await new Response(datei.stream().pipeThrough(new DecompressionStream('gzip'))).text()
      : await datei.text();
    const daten = stelleBinaeresHer(JSON.parse(text));
    return typeof pruefe === 'function' ? pruefe(daten) : pruefeAbzug(daten);
  },
  async schreib(abzug, dateiname = DATEINAME) {
    const blob = new Blob([JSON.stringify(abzug, ersetzeBinaeres, 1)],
      { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const verweis = document.createElement('a');
    verweis.href = url;
    verweis.download = dateiname;
    verweis.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return { ziel: dateiname, bytes: blob.size };
  },
};

/**
 * Uint8Array ist in JSON nicht vorgesehen — als Objekt mit Zahlenschlüsseln
 * gespeichert käme Unsinn zurück. Deshalb ein ausdrückliches Format.
 */
export function ersetzeBinaeres(schluessel, wert) {
  if (wert instanceof Uint8Array) return { __bytes: Array.from(wert) };
  return wert;
}

export function stelleBinaeresHer(wert) {
  if (Array.isArray(wert)) return wert.map(stelleBinaeresHer);
  if (wert && typeof wert === 'object') {
    if (Array.isArray(wert.__bytes)) return new Uint8Array(wert.__bytes);
    return Object.fromEntries(Object.entries(wert).map(([k, v]) => [k, stelleBinaeresHer(v)]));
  }
  return wert;
}

// -------------------------------------------------------------------- PKCE

export function base64url(bytes) {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function neuerVerifizierer(zufall = globalThis.crypto) {
  return base64url(zufall.getRandomValues(new Uint8Array(32)));
}

/** S256 nach RFC 7636 — gegen das Beispiel der Norm geprüft. */
export async function herausforderung(verifizierer, krypto = globalThis.crypto) {
  const summe = await krypto.subtle.digest('SHA-256', new TextEncoder().encode(verifizierer));
  return base64url(summe);
}

/**
 * Wo der `state` liegt, der den Rückweg sichert — beide Flüsse teilen ihn.
 *
 * Oberhalb seiner Verwender deklariert: Beide sind Funktionen und liefen erst
 * nach der Auswertung des Moduls, aber eine Konstante unterhalb ihres
 * Gebrauchs ist genau die Falle, die dieses Projekt schon einmal einen Abend
 * gekostet hat.
 */
export const ZUSTAND = 'tauchlogbuch.zustand';

/**
 * Dieselbe Datei, andere Form: das lokale Ziel wie ein Cloud-Adapter.
 *
 * `dateiAdapter` ist der Notausgang mit eigener Handhabung — `schreib` nimmt
 * einen Dateinamen, `lies` eine Datei. Wer ein Ziel *auswählen* können soll,
 * braucht überall dieselbe Form. Diese Hülle liefert sie, ohne den Notausgang
 * anzufassen: An ihm hängen „Abzug schreiben" und „Abzug einlesen".
 *
 * `lokal: true` ist kein Schmuck — die Ansicht erkennt daran, dass dieses Ziel
 * keine Anmeldung braucht und dass `lies()` eine Datei entgegennimmt, statt
 * sie sich zu holen.
 */
export function dateiZiel(
  { dateiname = DATEINAME, bereich = 'checkin', pruefe = null } = {},
) {
  return {
    name: 'Datei',
    lokal: true,
    lies: (datei) => dateiAdapter.lies(datei, prueferFuer(pruefe, bereich)),
    schreib: (daten) => dateiAdapter.schreib(daten, dateiname),
  };
}

/**
 * Der gemeinsame Teil beider Anbieter: Verifizierer würfeln, merken,
 * Zustimmungsseite aufrufen. Der Verifizierer liegt in sessionStorage — er ist
 * nur für diesen einen Anmeldeweg gültig und soll den Tab nicht überleben.
 */
export async function starteAnmeldung({ ziel, clientId, umleitung, bereich, zusatz = {} },
  umgebung = {}) {
  const speicher = umgebung.speicher ?? globalThis.sessionStorage;
  const gehZu = umgebung.gehZu ?? ((adresse) => globalThis.location.assign(adresse));
  const verifizierer = neuerVerifizierer(umgebung.krypto ?? globalThis.crypto);
  speicher.setItem('tauchlogbuch.pkce', verifizierer);

  // `state` auch hier, nicht nur im Ankerfluss. PKCE schuetzt den Tausch des
  // Codes; `state` schuetzt davor, dass ueberhaupt ein fremder Code
  // entgegengenommen wird — etwa einer, den jemand dem Browser unterschiebt,
  // damit das Logbuch am Ende in *sein* Konto abgleicht.
  const zustand = neuerVerifizierer(umgebung.krypto ?? globalThis.crypto);
  speicher.setItem(ZUSTAND, zustand);

  const adresse = new URL(ziel);
  adresse.search = new URLSearchParams({
    client_id: clientId,
    redirect_uri: umleitung,
    response_type: 'code',
    state: zustand,
    code_challenge: await herausforderung(verifizierer, umgebung.krypto ?? globalThis.crypto),
    code_challenge_method: 'S256',
    ...(bereich ? { scope: bereich } : {}),
    ...zusatz,
  }).toString();
  gehZu(adresse.toString());
  return adresse.toString();
}

/**
 * Der implizite Fluss: Das Zugriffsrecht kommt direkt zurück, ohne Tokentausch.
 *
 * Nötig für Google. Sein Discovery-Dokument nennt unter
 * `token_endpoint_auth_methods_supported` nur `client_secret_post` und
 * `client_secret_basic` — **nicht** `none`. Ein öffentlicher Client kann sich
 * dort also gar nicht ausweisen, und PKCE ist bei Google eine Ergänzung zur
 * Client-Authentifizierung, kein Ersatz dafür. Der Code-Fluss endet deshalb in
 * einem 400, gleich wie sorgfältig alles andere eingerichtet ist.
 *
 * `response_type=token` umgeht den Token-Endpunkt: Das Recht steht nach der
 * Zustimmung im Adressanker. Kein Geheimnis nötig — dafür läuft es nach etwa
 * einer Stunde ab und lässt sich nicht erneuern. Für einen Abgleich, den man
 * bewusst anstösst, ist das der richtige Handel.
 *
 * Statt des PKCE-Verifizierers sichert hier `state` den Rückweg: Er liegt in
 * sessionStorage und muss unverändert zurückkommen, sonst stammt die Antwort
 * nicht aus diesem Anmeldeweg.
 */
export function starteAnkerAnmeldung({ ziel, clientId, umleitung, bereich, zusatz = {} },
  umgebung = {}) {
  const speicher = umgebung.speicher ?? globalThis.sessionStorage;
  const gehZu = umgebung.gehZu ?? ((adresse) => globalThis.location.assign(adresse));
  const zustand = neuerVerifizierer(umgebung.krypto ?? globalThis.crypto);
  speicher.setItem(ZUSTAND, zustand);

  const adresse = new URL(ziel);
  adresse.search = new URLSearchParams({
    client_id: clientId,
    redirect_uri: umleitung,
    response_type: 'token',
    state: zustand,
    ...(bereich ? { scope: bereich } : {}),
    ...zusatz,
  }).toString();
  gehZu(adresse.toString());
  return adresse.toString();
}

/**
 * Was im Adressanker zurückkam — Recht, Fehler oder nichts.
 *
 * Diese App führt ihre eigenen Adressen hinter derselben Raute (`#/sync`).
 * Der Anker muss deshalb beides auseinanderhalten können: Steht dort keine
 * Anmeldung, kommt `null` zurück und der Router macht weiter wie immer.
 *
 * Ein Anker, dessen `state` nicht zum gemerkten passt, wird abgewiesen — er
 * stammt dann nicht aus dem Anmeldeweg, der hier begonnen hat.
 */
export function tokenAusAnker(anker, erwarteterZustand) {
  const roh = String(anker ?? '').replace(/^#/, '');
  if (!roh.includes('access_token=') && !roh.includes('error=')) return null;
  const felder = new URLSearchParams(roh);

  if (felder.has('error')) {
    const teile = [felder.get('error'), felder.get('error_description')].filter(Boolean);
    return { fehler: `Anmeldung abgelehnt: ${teile.join(' — ')}` };
  }

  const token = felder.get('access_token');
  if (!token) return null;
  if (!erwarteterZustand || felder.get('state') !== erwarteterZustand) {
    return { fehler: 'Die Antwort gehört nicht zu diesem Anmeldeweg.' };
  }
  const dauer = Number(felder.get('expires_in'));
  return { token, gueltigBis: Number.isFinite(dauer) && dauer > 0 ? dauer : null };
}

/**
 * Was in der Abfrage zurückkam — Code, Fehler oder nichts.
 *
 * Das Gegenstück zu `tokenAusAnker()` für den Code-Fluss. Ein Code ohne
 * passenden `state` gehört nicht zu diesem Anmeldeweg: Er ist alt, fremd, oder
 * jemand hat ihn untergeschoben. Vorher lief so einer einfach in den
 * Tokentausch und erzeugte dort eine Meldung über etwas ganz anderes.
 */
export function codeAusAbfrage(suche, erwarteterZustand) {
  const felder = new URLSearchParams(String(suche ?? '').replace(/^\?/, ''));

  if (felder.has('error')) {
    const teile = [felder.get('error'), felder.get('error_description')].filter(Boolean);
    return { fehler: `Anmeldung abgelehnt: ${teile.join(' — ')}` };
  }

  const code = felder.get('code');
  if (!code) return null;
  if (!erwarteterZustand || felder.get('state') !== erwarteterZustand) {
    return { fehler: 'Die Antwort gehört nicht zu diesem Anmeldeweg — '
      + 'bitte neu beginnen.' };
  }
  return { code };
}

/**
 * Warum die Anmeldung abgelehnt wurde — im Wortlaut des Anbieters.
 *
 * "Anmeldung abgelehnt (400)" sagt niemandem etwas, und der Grund lag die
 * ganze Zeit im Antwortkörper: Beide Anbieter halten sich an RFC 6749 und
 * schicken `error` und `error_description` mit. Google nennt dort etwa
 * `invalid_request` mit "client_secret is missing", Dropbox `invalid_grant`
 * bei einem verbrauchten Code — das ist der Unterschied zwischen "irgendwas
 * ist kaputt" und einer Anweisung, was zu tun ist.
 *
 * Der Körper darf alles sein: JSON, HTML einer Fehlerseite oder nichts.
 * Deshalb wird er defensiv gelesen; im schlimmsten Fall bleibt die Nummer
 * übrig, und das ist der Stand von vorher.
 */
export async function ablehnungsgrund(antwort) {
  const nummer = `Anmeldung abgelehnt (${antwort.status})`;
  let text = '';
  try {
    text = await antwort.text();
  } catch {
    return `${nummer}.`;
  }
  try {
    const daten = JSON.parse(text);
    const teile = [daten.error, daten.error_description].filter(Boolean);
    // Sauberes JSON ohne die beiden Felder: Dann steht dort nichts, was einem
    // Menschen hilft. `{}` roh anzuhaengen waere Rauschen, kein Grund.
    return teile.length ? `${nummer}: ${teile.join(' — ')}` : `${nummer}.`;
  } catch {
    // Kein JSON — dann ist der rohe Anfang immer noch mehr als nichts.
  }
  const roh = text.trim().replace(/\s+/g, ' ').slice(0, 200);
  return roh ? `${nummer}: ${roh}` : `${nummer}.`;
}

/**
 * Warum ein Datei-Zugriff scheiterte — der Grund steht im Körper, nicht in der Nummer.
 *
 * Dasselbe Versäumnis wie beim Anmeldeweg, eine Ebene später: Alle drei
 * Anbieter schreiben in den Fehlerkörper, was fehlt, und alle sieben
 * Aufrufstellen warfen es weg. Am echten Konto ergab das "Dropbox antwortet
 * mit 401." — wahr und nutzlos, denn 401 heisst hier zweierlei: abgelaufenes
 * Recht **oder** eine Berechtigung, die die App nie bekommen hat. Dropbox
 * unterscheidet beides sauber (`invalid_access_token` vs. `missing_scope`
 * samt `required_scope`), man muss nur hinsehen.
 */
export async function zugriffsgrund(antwort, name) {
  const nummer = `${name} antwortet mit ${antwort.status}`;
  let text = '';
  try {
    text = await antwort.text();
  } catch {
    return `${nummer}.`;
  }
  const grund = deuteFehler(text);
  // Bei 401 ist der naechste Handgriff immer derselbe, und ihn zu nennen ist
  // billiger, als ihn jedes Mal herzuleiten.
  const rat = antwort.status === 401
    ? ' Entweder ist das Recht abgelaufen oder der App fehlt eine Berechtigung —'
      + ' beim Anbieter die Rechte der App prüfen, dann neu verbinden.'
    : '';
  return grund ? `${nummer}: ${grund}.${rat}` : `${nummer}.${rat}`;
}

/**
 * Derselbe Satz, aber als Fehler, dem man ansieht, was zu tun ist.
 *
 * Bei 401 taugt das abgelegte Recht nicht mehr. Solange es in der Sitzung lag,
 * war das gleichgültig — mit dem Schliessen des Browsers war es ohnehin weg.
 * Seit es dauerhaft liegt, muss es weggeworfen werden: Sonst behauptet die
 * Oberfläche „verbunden", während jeder Zugriff scheitert.
 */
export async function zugriffsfehler(antwort, name) {
  const fehler = new Error(await zugriffsgrund(antwort, name));
  fehler.status = antwort.status;
  fehler.rechtWeg = antwort.status === 401;
  return fehler;
}

/** Die drei Fehlerformen der drei Anbieter auf einen Satz bringen. */
function deuteFehler(text) {
  let daten;
  try {
    daten = JSON.parse(text);
  } catch {
    // Kein JSON — Dropbox antwortet bei manchen Fehlern in blossem Text.
    return text.trim().replace(/\s+/g, ' ').slice(0, 200);
  }
  // Dropbox: `error_summary` ist der genaueste Satz, den es hier gibt. Der
  // Punkt am Ende gehoert zum Format ("missing_scope/."), nicht zum Inhalt.
  if (typeof daten.error_summary === 'string' && daten.error_summary.trim()) {
    return daten.error_summary.trim().replace(/\/\.*$/, '');
  }
  const fehler = daten.error;
  if (typeof fehler === 'string') {
    return [fehler, daten.error_description].filter(Boolean).join(' — ');
  }
  if (fehler && typeof fehler === 'object') {
    const teile = [fehler.code ?? fehler['.tag'], fehler.message].filter(Boolean);
    // Dropbox nennt bei missing_scope die fehlende Berechtigung beim Namen.
    if (fehler.required_scope) teile.push(`nötig: ${fehler.required_scope}`);
    return teile.join(' — ');
  }
  return '';
}

/** Den Code aus der Rückleitung gegen ein Zugriffstoken tauschen. */
export async function holeToken({ ziel, clientId, umleitung, code }, umgebung = {}) {
  const speicher = umgebung.speicher ?? globalThis.sessionStorage;
  const netz = umgebung.fetch ?? globalThis.fetch;
  const verifizierer = speicher.getItem('tauchlogbuch.pkce');
  if (!verifizierer) throw new Error('Der Anmeldeweg wurde unterbrochen — bitte neu beginnen.');
  const antwort = await netz(ziel, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      client_id: clientId,
      grant_type: 'authorization_code',
      code,
      code_verifier: verifizierer,
      redirect_uri: umleitung,
    }).toString(),
  });
  if (!antwort.ok) throw new Error(await ablehnungsgrund(antwort));
  speicher.removeItem('tauchlogbuch.pkce');
  const daten = await antwort.json();
  return daten.access_token;
}

// ---------------------------------------------------------------- Dropbox

const DROPBOX = {
  anmeldung: 'https://www.dropbox.com/oauth2/authorize',
  token: 'https://api.dropboxapi.com/oauth2/token',
  lesen: 'https://content.dropboxapi.com/2/files/download',
  schreiben: 'https://content.dropboxapi.com/2/files/upload',
  // Ausdruecklich anfragen statt sich auf die Voreinstellung der App zu
  // verlassen. Ohne `scope` gibt Dropbox das, was in der App-Konsole
  // angehakt ist — fehlt dort etwas, merkt man es erst beim Schreiben, als
  // 401. Mit `scope` sagt es schon die Zustimmungsseite. Mehr als diese
  // beiden braucht ein App-Ordner nicht.
  bereich: 'files.content.write files.content.read',
};

/**
 * Dropbox über einen App-Ordner.
 *
 * Die App wird bei Dropbox als "App folder" angelegt; alle Pfade sind dann
 * relativ zu diesem Ordner, und die App sieht nichts ausserhalb davon — dasselbe
 * Prinzip wie `drive.appdata`.
 *
 * `token` kommt aus `holeToken()` nach der Rückleitung. Ohne App-Key aus einer
 * Dropbox-App lässt sich der Weg nicht beginnen.
 */
// Der Dateiname wird hereingereicht, weil zwei Dateien nebeneinander liegen:
// die Ausweise (Geheimtext, aendert sich selten) und die Tauchgaenge. Stuende
// er fest, schriebe der Datenabgleich in die Check-in-Datei.
export function dropboxAdapter(
  { clientId, token = null, umleitung, fetch: netz, dateiname = DATEINAME,
    bereich = 'checkin', pruefe = null } = {},
) {
  const anfragen = netz ?? globalThis.fetch;
  const pfad = `/${dateiname}`;

  function pruefeToken() {
    if (!token) {
      throw new Error(clientId
        ? 'Noch nicht mit Dropbox verbunden.'
        : 'Es fehlt der App-Key aus der Dropbox-App.');
    }
  }

  return {
    name: 'Dropbox',
    anmelden(umgebung = {}) {
      if (!clientId) throw new Error('Es fehlt der App-Key aus der Dropbox-App.');
      return starteAnmeldung({
        ziel: DROPBOX.anmeldung,
        clientId,
        umleitung,
        bereich: DROPBOX.bereich,
        // token_access_type=online: kein Aktualisierungstoken, das irgendwo
        // liegen bliebe. Nach dem Schliessen des Tabs ist die Sitzung vorbei.
        zusatz: { token_access_type: 'online' },
      }, umgebung);
    },
    tausche(code, umgebung = {}) {
      return holeToken({ ziel: DROPBOX.token, clientId, umleitung, code }, umgebung);
    },
    async lies() {
      pruefeToken();
      const antwort = await anfragen(DROPBOX.lesen, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Dropbox-API-Arg': JSON.stringify({ path: pfad }),
        },
      });
      // 409 heisst bei Dropbox unter anderem "Datei gibt es nicht" — beim
      // ersten Abgleich der Normalfall, kein Fehler.
      if (antwort.status === 409) return null;
      if (!antwort.ok) throw await zugriffsfehler(antwort, 'Dropbox');
      return prueferFuer(pruefe, bereich)(stelleBinaeresHer(await antwort.json()));
    },
    async schreib(abzug) {
      pruefeToken();
      const koerper = JSON.stringify(abzug, ersetzeBinaeres);
      const antwort = await anfragen(DROPBOX.schreiben, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/octet-stream',
          'Dropbox-API-Arg': JSON.stringify({
            path: pfad, mode: 'overwrite', mute: true,
          }),
        },
        body: koerper,
      });
      if (!antwort.ok) throw await zugriffsfehler(antwort, 'Dropbox');
      return { ziel: pfad, bytes: koerper.length };
    },
  };
}

// ----------------------------------------------------------- Google Drive

const DRIVE = {
  anmeldung: 'https://accounts.google.com/o/oauth2/v2/auth',
  // Kein Token-Endpunkt: Google verlangt dort ein Geheimnis, das in einem
  // Browser nichts zu suchen hat. Siehe `starteAnkerAnmeldung`.
  dateien: 'https://www.googleapis.com/drive/v3/files',
  hochladen: 'https://www.googleapis.com/upload/drive/v3/files',
  bereich: 'https://www.googleapis.com/auth/drive.appdata',
};

/**
 * Google Drive über den App-Ordner (`drive.appdata`).
 *
 * Dieser Bereich ist für andere Apps unsichtbar und taucht im Drive des Nutzers
 * nicht als Ordner auf — genau richtig für eine Datei, die niemand von Hand
 * anfassen soll.
 */
export function driveAdapter(
  { clientId, token = null, umleitung, fetch: netz, dateiname = DATEINAME,
    bereich = 'checkin', pruefe = null } = {},
) {
  const anfragen = netz ?? globalThis.fetch;

  function pruefeToken() {
    if (!token) {
      throw new Error(clientId
        ? 'Noch nicht mit Google Drive verbunden.'
        : 'Es fehlt die OAuth-Client-ID aus dem Google-Cloud-Projekt.');
    }
  }

  async function suchDatei() {
    const adresse = `${DRIVE.dateien}?spaces=appDataFolder`
      + `&q=${encodeURIComponent(`name='${dateiname}'`)}&fields=files(id,modifiedTime)`;
    const antwort = await anfragen(adresse, { headers: { Authorization: `Bearer ${token}` } });
    if (!antwort.ok) throw await zugriffsfehler(antwort, 'Google Drive');
    return (await antwort.json()).files?.[0] ?? null;
  }

  return {
    name: 'Google Drive',
    // Google gibt das Recht im Adressanker zurück; einen Tokentausch gibt es
    // hier nicht, und deshalb auch kein `tausche`.
    ankerRueckweg: true,
    anmelden(umgebung = {}) {
      if (!clientId) {
        throw new Error('Es fehlt die OAuth-Client-ID aus dem Google-Cloud-Projekt.');
      }
      return starteAnkerAnmeldung({
        ziel: DRIVE.anmeldung, clientId, umleitung, bereich: DRIVE.bereich,
        zusatz: { prompt: 'consent', include_granted_scopes: 'true' },
      }, umgebung);
    },
    async lies() {
      pruefeToken();
      const datei = await suchDatei();
      if (!datei) return null;                 // erster Abgleich: dort liegt noch nichts
      const antwort = await anfragen(`${DRIVE.dateien}/${datei.id}?alt=media`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!antwort.ok) throw await zugriffsfehler(antwort, 'Google Drive');
      return prueferFuer(pruefe, bereich)(stelleBinaeresHer(await antwort.json()));
    },
    async schreib(abzug) {
      pruefeToken();
      const vorhanden = await suchDatei();
      const grenze = 'tauchlogbuch-grenze';
      const kopfteil = JSON.stringify(
        vorhanden ? {} : { name: dateiname, parents: ['appDataFolder'] },
      );
      const koerper = `--${grenze}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n`
        + `${kopfteil}\r\n--${grenze}\r\nContent-Type: application/json\r\n\r\n`
        + `${JSON.stringify(abzug, ersetzeBinaeres)}\r\n--${grenze}--`;
      const antwort = await anfragen(
        `${DRIVE.hochladen}${vorhanden ? `/${vorhanden.id}` : ''}?uploadType=multipart`,
        {
          method: vorhanden ? 'PATCH' : 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': `multipart/related; boundary=${grenze}`,
          },
          body: koerper,
        },
      );
      if (!antwort.ok) throw await zugriffsfehler(antwort, 'Google Drive');
      return { ziel: dateiname, bytes: koerper.length };
    },
  };
}

// --------------------------------------------------------------- OneDrive

const ONEDRIVE = {
  anmeldung: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
  token: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
  // Der App-Ordner heisst bei Microsoft `approot` — das Gegenstück zu Dropbox'
  // "App folder" und Googles `drive.appdata`: ein eigener Bereich, vom übrigen
  // Konto getrennt.
  inhalt: (name) => 'https://graph.microsoft.com/v1.0/me/drive/special/approot:/'
    + `${encodeURIComponent(name)}:/content`,
  // Ohne `offline_access`: Das gäbe ein Erneuerungsrecht zurück, und
  // `holeToken()` wirft es weg. Ein Recht anzufordern, das man nicht benutzt,
  // ist beim Anmelden eine Zeile mehr in der Zustimmung und sonst nichts.
  bereich: 'Files.ReadWrite.AppFolder',
};

/**
 * OneDrive über den App-Ordner (`Files.ReadWrite.AppFolder`).
 *
 * Der einfachste der drei Anbieter — und der Gegenbeweis zu Google: Microsoft
 * lässt öffentliche Clients an den Tokenendpunkt, sobald die Weiterleitung in
 * der App-Registrierung als Typ **`spa`** eingetragen ist. Dann ist dort CORS
 * offen und PKCE ohne Geheimnis der vorgesehene Weg. OneDrive läuft deshalb
 * über denselben Code-Fluss wie Dropbox; ein dritter Anmeldeweg entsteht nicht.
 *
 * Ist die Weiterleitung als Typ „Web" eingetragen, verlangt Microsoft ein
 * Geheimnis und CORS blockt — dieselbe Sackgasse wie bei Google.
 */
export function onedriveAdapter(
  { clientId, token = null, umleitung, fetch: netz, dateiname = DATEINAME,
    bereich = 'checkin', pruefe = null } = {},
) {
  const anfragen = netz ?? globalThis.fetch;

  function pruefeToken() {
    if (!token) {
      throw new Error(clientId
        ? 'Noch nicht mit OneDrive verbunden.'
        : 'Es fehlt die Anwendungs-ID aus der App-Registrierung.');
    }
  }

  return {
    name: 'OneDrive',
    anmelden(umgebung = {}) {
      if (!clientId) {
        throw new Error('Es fehlt die Anwendungs-ID aus der App-Registrierung.');
      }
      return starteAnmeldung({
        ziel: ONEDRIVE.anmeldung, clientId, umleitung, bereich: ONEDRIVE.bereich,
        zusatz: { response_mode: 'query' },
      }, umgebung);
    },
    tausche(code, umgebung = {}) {
      return holeToken({ ziel: ONEDRIVE.token, clientId, umleitung, code }, umgebung);
    },
    async lies() {
      pruefeToken();
      const antwort = await anfragen(ONEDRIVE.inhalt(dateiname),
        { headers: { Authorization: `Bearer ${token}` } });
      // 404 heisst hier nicht Fehler, sondern: dort liegt noch nichts.
      if (antwort.status === 404) return null;
      if (!antwort.ok) throw await zugriffsfehler(antwort, 'OneDrive');
      return prueferFuer(pruefe, bereich)(stelleBinaeresHer(await antwort.json()));
    },
    async schreib(daten) {
      pruefeToken();
      const inhalt = JSON.stringify(daten, ersetzeBinaeres);
      const antwort = await anfragen(ONEDRIVE.inhalt(dateiname), {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: inhalt,
      });
      if (!antwort.ok) throw await zugriffsfehler(antwort, 'OneDrive');
      return { ziel: dateiname, bytes: inhalt.length };
    },
  };
}

export const ANBIETER = {
  dropbox: { name: 'Dropbox', baue: dropboxAdapter,
    hinweis: 'App-Key aus einer Dropbox-App (Typ "App folder")' },
  drive: { name: 'Google Drive', baue: driveAdapter,
    hinweis: 'OAuth-Client-ID aus einem Google-Cloud-Projekt' },
  onedrive: { name: 'OneDrive', baue: onedriveAdapter,
    hinweis: 'Anwendungs-ID einer App-Registrierung, Weiterleitung als Typ "spa"' },
};
